/* ══════════════════════════════════════════════════════════
   AZN MILANO — Quick Add (card hover → 2-step color/size)
   ══════════════════════════════════════════════════════════ */

(() => {
  const TOAST_CLASS = 'azn-toast';

  const ensureToastStyles = () => {
    if (document.getElementById('azn-toast-styles')) return;
    const style = document.createElement('style');
    style.id = 'azn-toast-styles';
    style.textContent =
      `.${TOAST_CLASS}{position:fixed;left:50%;bottom:32px;transform:translateX(-50%) translateY(20px);` +
      `background:#0e0e0e;color:#fff;border:1px solid #ff6b00;border-radius:4px;padding:14px 22px;` +
      `font:600 13px/1 'Inter',system-ui,sans-serif;letter-spacing:.08em;text-transform:uppercase;` +
      `z-index:99999;opacity:0;pointer-events:none;transition:opacity .25s ease,transform .25s ease;` +
      `box-shadow:0 10px 30px rgba(0,0,0,.5);}` +
      `.${TOAST_CLASS}.show{opacity:1;transform:translateX(-50%) translateY(0);}` +
      `.${TOAST_CLASS}.error{border-color:#ff3a3a;}`;
    document.head.appendChild(style);
  };

  const showToast = (message, isError = false) => {
    ensureToastStyles();
    const toast = document.createElement('div');
    toast.className = `${TOAST_CLASS}${isError ? ' error' : ''}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('show'));
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  };

  const notifyCartUpdate = async (json) => {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: json }));
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
      try {
        const cartData = await fetch('/cart.js').then((r) => r.json());
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'azn-quick-add',
          productVariantId: json?.variant_id,
          cartData,
        });
      } catch (_) { /* silent: cart:refresh already fired */ }
    }
  };

  const getVariants = (panel) => {
    if (panel._variants) return panel._variants;
    try {
      panel._variants = JSON.parse(panel.dataset.variants || '[]');
    } catch (e) {
      panel._variants = [];
    }
    return panel._variants;
  };

  const getSelectedColor = (panel) => panel.querySelector('.card-color-btn.selected')?.dataset.color || null;
  const getSelectedSize = (panel) => panel.querySelector('.card-size-btn.selected:not([disabled])')?.dataset.size || null;

  const refreshSizeAvailability = (panel) => {
    const variants = getVariants(panel);
    const selectedColor = getSelectedColor(panel);
    const sizeBtns = panel.querySelectorAll('.card-size-btn');
    if (!sizeBtns.length) return;

    sizeBtns.forEach((btn) => {
      const size = btn.dataset.size;
      let available = false;
      if (selectedColor !== null) {
        // Need a variant matching color + size that is available
        available = variants.some((v) => v.color === selectedColor && v.size === size && v.available);
      } else {
        // No color selected: enable any size that has at least one available variant
        available = variants.some((v) => v.size === size && v.available);
      }
      if (available) {
        btn.removeAttribute('disabled');
      } else {
        btn.setAttribute('disabled', '');
        btn.classList.remove('selected');
      }
    });
  };

  const findVariant = (panel) => {
    const variants = getVariants(panel);
    const color = getSelectedColor(panel);
    const size = getSelectedSize(panel);
    const hasColor = panel.querySelectorAll('.card-color-btn').length > 0;
    const hasSize = panel.querySelectorAll('.card-size-btn').length > 0;

    return variants.find((v) => {
      if (hasColor && v.color !== color) return false;
      if (hasSize && v.size !== size) return false;
      return v.available;
    });
  };

  const addToCart = async (panel, addBtn) => {
    if (addBtn.disabled) return;
    const hasColor = panel.querySelectorAll('.card-color-btn').length > 0;
    const hasSize = panel.querySelectorAll('.card-size-btn').length > 0;

    if (hasColor && !getSelectedColor(panel)) {
      showToast('Scegli un colore', true);
      return;
    }
    if (hasSize && !getSelectedSize(panel)) {
      showToast('Scegli una taglia', true);
      return;
    }

    const variant = findVariant(panel);
    if (!variant) {
      showToast('Combinazione non disponibile', true);
      return;
    }

    addBtn.disabled = true;
    addBtn.classList.add('is-loading');
    const originalText = addBtn.textContent;
    addBtn.textContent = '...';

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const sectionsToRender = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map((s) => s.id)
        : ['cart-drawer', 'cart-icon-bubble'];

      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: 1 }],
          sections: sectionsToRender.join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.description || data?.message || 'error');
      }
      const json = await res.json();
      showToast('Aggiunto al carrello');

      // Use Dawn's native renderContents to refresh cart-drawer + icon-bubble AND open the drawer
      if (cartDrawer && json.sections && typeof cartDrawer.renderContents === 'function') {
        // Cart is no longer empty — strip the initial-state is-empty class from <cart-drawer>
        // (renderContents only swaps inner HTML; outer classes persist from first page render)
        cartDrawer.classList.remove('is-empty');
        // renderContents expects { id, sections: {...} } — the first item carries id
        const parsed = json.items && json.items[0] ? { ...json.items[0], sections: json.sections } : { id: variant.id, sections: json.sections };
        cartDrawer.renderContents(parsed);
      } else {
        // Fallback: pub-sub notification for any subscriber
        await notifyCartUpdate(json.items && json.items[0] ? json.items[0] : json);
      }
    } catch (err) {
      const msg = /sold out|unavailable|not available/i.test(err.message)
        ? 'Esaurito'
        : 'Errore, riprova';
      showToast(msg, true);
    } finally {
      addBtn.disabled = false;
      addBtn.classList.remove('is-loading');
      addBtn.textContent = originalText;
    }
  };

  const init = () => {
    document.addEventListener('click', (e) => {
      const colorBtn = e.target.closest('.card-quick .card-color-btn');
      if (colorBtn) {
        e.preventDefault();
        e.stopPropagation();
        const panel = colorBtn.closest('.card-quick');
        panel.querySelectorAll('.card-color-btn').forEach((b) => b.classList.remove('selected'));
        colorBtn.classList.add('selected');
        refreshSizeAvailability(panel);
        return;
      }

      const sizeBtn = e.target.closest('.card-quick .card-size-btn');
      if (sizeBtn && !sizeBtn.hasAttribute('disabled')) {
        e.preventDefault();
        e.stopPropagation();
        const panel = sizeBtn.closest('.card-quick');
        panel.querySelectorAll('.card-size-btn').forEach((b) => b.classList.remove('selected'));
        sizeBtn.classList.add('selected');
        return;
      }

      const addBtn = e.target.closest('.card-quick [data-add]');
      if (addBtn) {
        e.preventDefault();
        e.stopPropagation();
        const panel = addBtn.closest('.card-quick');
        addToCart(panel, addBtn);
      }
    });

    // Initialize size availability on every panel (no color selected → enable all available sizes)
    document.querySelectorAll('.card-quick').forEach(refreshSizeAvailability);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
